import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, } from '@angular/core';
import { Currency } from 'app/models/currency.model';
import { Observable } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { MatDialog } from "@angular/material/dialog";
import { MenuEventModalComponent } from 'app/pages/menu/shared/menu-event-modal/menu-event-modal.component';
import {
  DisplayedProduct,
  DisplayedSize,
  DisplayedSizeStructure,
  MenuSection,
  Product,
  Size
} from 'app/models/menu.model';
import { Store } from '@ngrx/store';
import { selectDictionarySizes, selectOriginalDictionarySizes } from 'app/reducers/menu.selectors';
import {
  MenuSchedulesEntityModalComponent,
  MenuSchedulesEntityType,
  MenuSchedulesModalData
} from 'app/pages/menu/shared/menu-schedules-entity-modal/menu-schedules-entity-modal.component';
import {
  SendProductModalComponent,
  SendProductModalComponentData
} from 'app/pages/menu/shared/send-product-modal/send-product-modal.component';
import { PipeConfiguration } from 'app/models/pipe.model';
import { Organization } from 'app/models/organization.model';

@UntilDestroy()
@Component({
  selector: 'app-menu-section-product[product][originalProduct][menuSection][currentOrganization][menuPipes]',
  templateUrl: './section-product.component.html',
  styleUrls: ['./section-product.component.scss']
})
export class SectionProductComponent implements OnInit, OnChanges {
  defaultPicture = 'assets/images/dishes/logo.png';

  @Input() product: DisplayedProduct;
  @Input() originalProduct: Product;
  @Input() currency: Currency | null;

  @Input() menuSection: MenuSection;
  @Input() currentOrganization: Organization;
  @Input() menuPipes: PipeConfiguration[];
  // Показывать ли warning "блюдо не будет отображаться на киоске" — задаётся родительским
  // section-products, которому в свою очередь задаёт menu-main-content.
  @Input() showKioskWarning: boolean = false;
  // Разрешено ли редактировать продукт (поля) и отправлять его в пайп. Рассчитывается
  // родителем из права ProductEdit (IC-2972) и backup-контекста меню-секции.
  @Input() canEditProduct: boolean = true;

  @Output() editProduct = new EventEmitter<Product>();

  dictionarySizes$: Observable<Map<string, DisplayedSize>>;
  originalDictionarySizes$: Observable<Map<string, Size>>;

  priceValue = 0;
  hasPrice = true;
  productName = '';
  sizes: DisplayedSizeStructure[];
  dictionarySizes: Map<string, DisplayedSize>;
  originalDictionarySizes: Map<string, Size>;

  constructor(
    public dialog: MatDialog,
    private store: Store,
  ) {
  }

  ngOnInit(){
    this.dictionarySizes$ = this.store.select(selectDictionarySizes);
    this.originalDictionarySizes$ = this.store.select(selectOriginalDictionarySizes);

    this.dictionarySizes$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(dictionarySizes => {
        this.dictionarySizes = dictionarySizes;
      });

    this.originalDictionarySizes$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(originalDictionarySizes => {
        this.originalDictionarySizes = originalDictionarySizes;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.product.productStructure) {
      this.loadProductWithOverrides();
    } else if (this.product.comboStructure) {
      this.loadComboWithOverrides();
    }
  }

  loadProductWithOverrides(): void {
    this.priceValue = this.product.productStructure.price;
    if (this.product.productStructure.sizes.length > 0) {
      this.hasPrice = false;
      this.sizes = this.product.productStructure.sizes.filter(s => !s.isHidden).sort((a,b) => a.order - b.order);
    }
    this.productName = this.product.name;
  }

  loadComboWithOverrides(): void {
    this.priceValue = this.product.comboStructure.price;
    this.productName = this.product.name;
  }

  onOpenEditProductDialog(product: Product) {
    this.editProduct.emit(product);
  }

  onOpenMenuEventProductDialog(product: Product) {
    this.dialog.open(MenuEventModalComponent, {
      minWidth: '50%',
      maxWidth: '1040px',
      data: {
        menuSectionId: this.menuSection.id,
        positionId: this.product.externalId,
      }
    });
  }

  onOpenSendProductDialog(product: Product) {
    const data: SendProductModalComponentData = {
      menuSection: this.menuSection,
      productId: this.product.externalId,
      pipes: this.menuPipes,
      organization: this.currentOrganization,
    };
    this.dialog.open(SendProductModalComponent, {
      minWidth: '50%',
      maxWidth: '1040px',
      data,
    });
  }

  getSizeName(sizeId: string) {
    return this.dictionarySizes.get(sizeId)?.name?.substring(0,10);
  }

  isChangedSize(sizeId: string): boolean{
    return this.dictionarySizes.get(sizeId).isChangedSize;
  }

  onOpenSchedulesProductDialog() {
    this.dialog.open<MenuSchedulesEntityModalComponent, MenuSchedulesModalData>(
      MenuSchedulesEntityModalComponent,
      {
        data: {
          menuSectionId: this.menuSection.id,
          entityId: this.product.externalId,
          entityType: MenuSchedulesEntityType.PRODUCT,
        }
      });
  }
}
